import { createAction } from '@reduxjs/toolkit'

import type { AqsiRecoverAction } from './aqsiStateTypes'
import type { PaymentKind } from './types'

/**
 * Команды окна продажи — plain actions (F05): экран → actions → команда →
 * listener → запрос. Ключ идемпотентности создаёт listener ОДИН раз на
 * нажатие. Действующее лицо в команде не передаётся: его знает только токен.
 */
interface Seat {
  resourceId: string
  registerKey: string
  deviceId: number
}

interface SaleRef extends Seat {
  saleId: string
  /** Версия продажи, которую видел человек. */
  expectedVersion: number
}

/** Запустить очередь корзины: отправить черновик (после паузы debounce, перед оплатой, после F5). Идущую отправку не трогает. */
export const cartFlushRequested = createAction<Seat>('aqsi/sale/cartFlushRequested')
/** «Повторить сейчас»: отправка корзины ждёт паузу перед повтором — повторить немедленно, тем же запросом. */
export const cartRetryRequested = createAction<Seat>('aqsi/sale/cartRetryRequested')
/**
 * «Вернуть как на сервере»: кассир отказывается от несохранённой правки (служба отвергла её или связи нет). Повтор останавливается,
 * корзина показывается такой, как её знает сервер; если неподтверждённый запрос всё же дошёл, перечитанная продажа его покажет.
 */
export const cartDiscardRequested = createAction<Seat>('aqsi/sale/cartDiscardRequested')
export const salePayRequested = createAction<SaleRef>('aqsi/sale/payRequested')
export const saleFiscalizeRequested = createAction<SaleRef>('aqsi/sale/fiscalizeRequested')
export const saleCancelRequested = createAction<SaleRef>('aqsi/sale/cancelRequested')
/** «Отменить» оплату картой на терминале. Не опасная команда: просьба кабинету безопасна для повтора, итог читается операцией. */
export const salePaymentCancelRequested = createAction<SaleRef>('aqsi/sale/paymentCancelRequested')
/**
 * Решение по слипу, найденному после отмены оплаты (внимание `late-payment-slip`): `paid` - «Оплата прошла — пробить чек», иначе
 * «Оплаты не было». Один POST на нажатие; двойное нажатие гасит набор в памяти. `background` — отложенная продажа из «Чеков в фоне»: её итог —
 * в общем итоге команд кассы, а не в окне оплаты активной продажи.
 */
export const saleLatePaymentRequested = createAction<SaleRef & { paid: boolean; background?: boolean }>('aqsi/sale/latePaymentRequested')
/** «Привязать уже пробитый чек»: причина обязательна, номер чека кабинета — по желанию. Один POST на нажатие. */
export const saleIssuedOutsideRequested = createAction<SaleRef & { reason: string; receiptId: string | null }>(
  'aqsi/sale/issuedOutsideRequested',
)
/** «Отложить продажу» (RP-FB35 K9): причина необязательна. Один POST на нажатие. */
export const saleParkRequested = createAction<SaleRef & { reason: string | null }>('aqsi/sale/parkRequested')
/** «Вернуть на кассу» из списка отложенных: `expectedVersion` — версия отложенной продажи из списка. */
export const saleUnparkRequested = createAction<SaleRef>('aqsi/sale/unparkRequested')
/**
 * «Чека нет — пробить заново» (RP-FB35 K3/K7): новая попытка чека по слову человека. `checked` — что проверено (кассу, кабинет), `reason` —
 * почему чека нет; обе записываются в журнал навсегда. Один POST на нажатие; двойное нажатие даёт одну команду.
 */
export const saleReceiptRetryRequested = createAction<SaleRef & { reason?: string; checked?: string }>(
  'aqsi/sale/receiptRetryRequested',
)
/**
 * «Актуализировать состояние» / «Проверить ещё раз» панели «Проверка в AQSI» (RP-CASH-RECOVERY §4.1): только ЧТЕНИЕ — служба заново
 * читает AQSI, ничего не отправляя кассе.
 */
export const saleAqsiStateCheckRequested = createAction<{ resourceId: string; saleId: string }>('aqsi/sale/aqsiStateCheckRequested')
/**
 * Сменить способ оплаты. У продажи он задан при создании: для существующей
 * корзины это отмена (денег и чека не было — доказанно) и новая продажа теми же позициями.
 */
export const salePaymentKindRequested = createAction<
  Seat & {
    kind: PaymentKind
    sale: { id: string; version: number; lines: readonly SaleSnapshotLine[] } | null
  }
>('aqsi/sale/paymentKindRequested')
/** «Проверить статус»: ЧТЕНИЕ продажи и операции, а не повтор команды. */
export const saleStatusCheckRequested = createAction<Seat & { operationId: string | null }>(
  'aqsi/sale/statusCheckRequested',
)
/** «Сверить с кабинетом»: служба только читает операцию кабинета. `automatic` — опрос хода, без баннера. */
export const saleReconcileRequested = createAction<
  Seat & { operationId: string; expectedVersion: number; automatic: boolean; saleId?: string | null }
>('aqsi/sale/reconcileRequested')
export const saleReloadRequested = createAction<Seat>('aqsi/sale/reloadRequested')
/** Снять ожидающий поиск товара из очереди кассы (RP-SALE-FAST, шаг 7а). */
export const lookupCancelRequested = createAction<Seat & { id: string }>('aqsi/sale/lookupCancelRequested')
/** Код со сканера окна продажи этого кабинета. */
export const saleBarcodeScanned = createAction<{ resourceId: string; code: string }>('aqsi/sale/barcodeScanned')

/** Позиция корзины, как её видит человек: для смены способа оплаты и «Отложить». */
export interface SaleSnapshotLine {
  productKey: string
  barcode: string | null
  quantity: string
  /** Цена кассира в рублях; `null` или нет — цена кабинета (RP-FB8). */
  price?: string | null
}

/**
 * UI-RMK: «Наличными» / «Картой» — ОДНО нажатие, как в старом РМК. Нажатие не зависит от того, сохранена ли корзина и что читается в
 * фоне (RP-SALE-FAST, шаг 4): окно оплаты открывается сразу, оплата дожидается очереди корзины, берёт свежую продажу из кэша и дальше идёт
 * прежним путём — проверка связи с кассой, смена способа вместе с корзиной (через очередь), одна команда оплаты; у карты после доказанной
 * оплаты чек отправляет служба.
 */
export const salePayWithRequested = createAction<Seat & { kind: PaymentKind }>('aqsi/sale/payWithRequested')

/** «Отменить» в подготовке оплаты (корзина сохраняется, связь проверяется): деньги не уходили — попытка закрывается, оплата не начнётся. */
export const salePreparationCancelRequested = createAction<Seat>('aqsi/sale/preparationCancelRequested')

/** UI-RMK: строка поиска РМК — Enter: первый найденный товар (штрихкод целиком — он) в чек. */
export const saleSearchSubmitted = createAction<Seat & { text: string }>('aqsi/sale/searchSubmitted')

/** Товар есть в общем каталоге, но не в кассе: «Отправить в кассу сейчас» — раздача этого одного товара, затем он идёт в чек. */
export const saleDeliveryRequested = createAction<Seat>('aqsi/sale/deliveryRequested')

/** UI-RMK: «Отложить» — продажа места отменяется, позиции становятся отложенным чеком кассы (сервер). */
export const saleHoldRequested = createAction<SaleRef>('aqsi/sale/holdRequested')

/**
 * UI-RMK: «Открыть» отложенный чек. Текущий чек с позициями сначала
 * откладывается (так в старом РМК: чеки меняются местами), затем отложенный
 * становится продажей кассы.
 */
export const heldCartRestoreRequested = createAction<Seat & { heldCartId: string; current: { saleId: string; version: number } | null }>(
  'aqsi/sale/heldCartRestoreRequested',
)

/** UI-RMK: «Удалить» отложенный чек — покупатель не вернулся. */
export const heldCartDropRequested = createAction<Seat & { heldCartId: string; version: number }>('aqsi/sale/heldCartDropRequested')

/** Ручная сверка операции с неизвестным исходом — один POST на нажатие. */
export const operationResolveRequested = createAction<{
  resourceId: string
  operationId: string
  expectedVersion: number
  outcome: 'Succeeded' | 'Failed'
  reason: string
  checked: string
  resultId: string | null
  /** Сверка из окна оплаты кассы: итог — в окне попытки; нет — отдельное окно сверки (возврат, инциденты, чеки в фоне). */
  seat?: { registerKey: string; saleId: string | null }
}>('aqsi/sale/operationResolveRequested')
export const operationResolveCheckRequested = createAction<{ resourceId: string; operationId: string }>(
  'aqsi/sale/operationResolveCheckRequested',
)

/**
 * «Пробить чек» из сверки операции чека по галочкам (К-128): деньги списаны, чека в ФН нет. Один POST `receipt/recover` на нажатие;
 * `operationId` — операция чека, по черновику сверки которой идёт ход. `inWindow` — сверка из окна оплаты: итог в окне попытки; иначе —
 * в итоге отдельного окна сверки.
 *
 * Основное действие панели «Проверка в AQSI» (К-129) — та же команда с кодом `action`, который служба назвала основным; `checked` у неё
 * нет (это не слово человека), а `operationId` — операция чека продажи, если она известна (`null` — черновика сверки нет).
 */
export const saleReceiptRecoverRequested = createAction<
  SaleRef & { operationId: string | null; checked?: string; inWindow: boolean; action?: Exclude<AqsiRecoverAction, 'unknown'> }
>('aqsi/sale/receiptRecoverRequested')

/**
 * «Деньги не списаны» из сверки операции чека (К-128), когда оплата системой не доказана: сверка операции ОПЛАТЫ продажи «не исполнена»
 * — прежняя ручная сверка; продажа и операция оплаты (её версия) читаются перед записью. `operationId` — операция чека (черновик и итог
 * окна сверки).
 */
export const salePaymentNotTakenRequested = createAction<{
  resourceId: string
  operationId: string
  saleId: string
  seat?: { registerKey: string; saleId: string | null }
}>('aqsi/sale/paymentNotTakenRequested')
